// Talent Management — tab 1: the BI-backed dashboard.
//
// The page sends a SELECTION (range, filters, sort, page) and renders the answer.
// It used to receive the whole seller table and fold it in the browser; that grew
// with the warehouse until it crossed Cloud Run's 32 MiB response cap and took the
// dashboard down for four days (issue #1211). The fold now runs in SQL and the
// response is one page plus the totals over everything the selection matches.
//
// So a filter, a sort or a Show more is a round trip now, where it used to be free.
// That is the trade this design makes deliberately: at ~10-30 dashboard loads a day
// it costs an internal analyst a moment, and it is what makes the payload bounded by
// the PAGE rather than by the table (root §1.16).
//
// Deliberately mirrors the Customer Support page (stat card grid, `rc-table`,
// TableSkeleton, AreaBoundary) so the two areas read as one product.
import {
  Badge,
  Button,
  Card,
  type DateRange,
  DateRangeFilter,
  Input,
  MultiSelect,
  type MultiSelectOption,
  rangeForDays,
  Skeleton,
  TableExport,
  useRestoredState,
} from "@recruiter/shared";
import type { TalentQueryResult, TalentSellerAggregate } from "@recruiter/shared/types";
import { ShowMoreRow } from "../components/show-more-row";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AreaBoundary } from "../components/area-gate";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { ExplorerTalentLink } from "../components/explorer-link";
import { formatStatValue } from "../lib/format";
import { subCategoryLabel } from "../lib/sub-categories";
import { useDebounced } from "../lib/use-debounced";
import {
  EMPTY_FILTERS,
  PAGE_SIZE,
  UNKNOWN,
  hasActiveFilters,
  selectionFor,
  sellerRates,
  stripFigures,
  type Filters,
  type Ratio,
  type SortKey,
} from "../lib/talent";
import { useTalentResult } from "../lib/use-talent-result";
import "./talent-management.css";

/** A rate as "42%", with the raw parts on hover. Unknown renders as a dash, never
 *  0%: a seller we never approached has no score, and showing 0% would libel them.
 *
 *  `unknownTitle` names the MISSING DENOMINATOR, which differs per column now that
 *  willingness divides by replies rather than approaches: "no approaches" on a
 *  column that divides by something else is a wrong explanation, not a generic one. */
function Pct({ ratio, unknownTitle }: { ratio: Ratio; unknownTitle?: string }) {
  if (ratio.value === null) {
    return (
      <span className="tm-unknown" title={unknownTitle ?? "No approaches in this window"}>
        -
      </span>
    );
  }
  return (
    <span title={`${ratio.numerator} of ${ratio.denominator}`}>
      {Math.round(ratio.value * 100)}%
    </span>
  );
}

/** Minutes, humanized, as text: the ONE duration ladder on this page. The strip
 *  cards need the same string the table cells render (a card and the column under
 *  it describing the same quantity in different units is a misread waiting to
 *  happen), and each card now prints TWO of them, so the ladder lives here rather
 *  than being spelled out at every call site. */
function minutesText(value: number): string {
  if (value < 60) return `${Math.round(value)}m`;
  if (value < 60 * 24) return `${(value / 60).toFixed(1)}h`;
  return `${(value / 1440).toFixed(1)}d`;
}

/** The same ladder for a value that may be unmeasured. A dash, never "0m", which
 *  would read as instant. */
function minutesTextOrDash(value: number | null): string {
  return value === null ? "-" : minutesText(value);
}

/** Minutes, humanized. Unknown is a dash — "0m" would read as instant. */
function Minutes({ value }: { value: number | null | undefined }) {
  if (value == null)
    return (
      <span className="tm-unknown" title="Not measured">
        -
      </span>
    );
  return <>{minutesText(value)}</>;
}

/** A dimension cell. Null is UNKNOWN and says so; it is never blank (which reads
 *  as a rendering bug) and never a substituted bucket. */
function Dim({ value }: { value: string | number | null | undefined }) {
  if (value == null || value === "") {
    return (
      <Badge tone="neutral" size="sm">
        Unknown
      </Badge>
    );
  }
  return <>{value}</>;
}

/** A strip card. When the metric is an AVERAGE, `median` is its middle, rendered
 *  as a figure of its own beside the mean rather than folded into the hint
 *  sentence: the two answer different questions about the same population (the
 *  mean is thread-weighted, so one prolific straggler drags it; the median is the
 *  middle seller, which no single whale can move), and a reader comparing them is
 *  doing the thing this pair exists for. It spent a release inside the hint text
 *  and was, reasonably, read as absent.
 *
 *  Pass "-" rather than omitting when a median is DEFINED for the metric but
 *  unmeasured — an average with the median silently missing beside it is the
 *  ambiguity this whole change removes. Omit only where no median exists. */
function StatCard({
  label,
  value,
  median,
  hint,
}: {
  label: string;
  value: string;
  median?: string;
  hint?: string;
}) {
  return (
    <Card padding="md" className="tm-stat">
      <span className="tm-stat__label">{label}</span>
      <span className="tm-stat__figures">
        <strong className="tm-stat__value">{value}</strong>
        {median === undefined ? null : (
          <span className="tm-stat__median">
            <span className="tm-stat__median-key">median</span>
            {median}
          </span>
        )}
      </span>
      {hint ? <span className="tm-stat__hint">{hint}</span> : null}
    </Card>
  );
}

function Provenance({
  result,
  range,
  refreshing,
}: {
  result: TalentQueryResult;
  range: DateRange;
  refreshing: boolean;
}) {
  const snapshot = result;
  // Freshness is warehouse time, not fetch time. A reader who only sees "loaded
  // just now" is being told something true about the browser and nothing about
  // the data.
  const source = snapshot.sourceUpdatedAt ?? snapshot.generatedAt;
  const crossesG4 = !range.from || range.from < snapshot.g4WatermarkAt.slice(0, 10);
  return (
    <div className="tm-provenance">
      <span>
        Warehouse data as of{" "}
        <strong>{source ? source.replace("T", " ").slice(0, 16) : "unknown"}</strong>
      </span>
      {refreshing ? (
        // Says what is happening, and does not promise when it lands: the rebuild
        // may be sharing another page load's job, and it may fail. If it does, the
        // timestamp above simply stays put, which is the honest outcome.
        <Badge tone="neutral" size="sm">
          Checking for newer data
        </Badge>
      ) : null}
      <span>
        Dimensions known for <strong>{snapshot.dimsAvailablePct.toFixed(0)}%</strong> of rows
      </span>
      {result.replyIncludesAutoRepliesCaveat ? (
        <Badge tone="warning" size="sm">
          Response counts include automatic replies and PA messages
        </Badge>
      ) : null}
      {snapshot.searchTierSource !== "observed" ? (
        <Badge tone="neutral" size="sm">
          Search Tier not yet recorded for this period
        </Badge>
      ) : null}
      {snapshot.isManagedSource === "proxy" ? (
        <Badge tone="neutral" size="sm">
          Is-managed is an approximation
        </Badge>
      ) : null}
      {crossesG4 ? (
        <Badge tone="neutral" size="sm">
          Response times before {snapshot.g4WatermarkAt.slice(0, 10)} may read up to 30 min high
        </Badge>
      ) : null}
    </div>
  );
}

const COLUMNS: { key: SortKey; label: string; numeric?: boolean; title?: string }[] = [
  { key: "sellerId", label: "Seller" },
  { key: "searchTier", label: "Tier" },
  { key: "searchTierPoints", label: "Tier score", numeric: true },
  { key: "country", label: "Country" },
  { key: "sellerLevel", label: "Level" },
  { key: "subCategoryId", label: "Sub-category" },
  { key: "approached", label: "Approached", numeric: true },
  {
    key: "responseRate",
    label: "Response rate",
    numeric: true,
    title: "Approaches the talent replied to, over every approach",
  },
  { key: "avgResponseMinutes", label: "Avg response", numeric: true },
  { key: "avgTimeToOfferMinutes", label: "Avg to offer", numeric: true },
  { key: "questions", label: "Questions", numeric: true },
  {
    key: "willingness",
    label: "Willingness",
    numeric: true,
    // The two rate columns divide by DIFFERENT things, so each says which. Without
    // this a reader compares 10% response with 35% willingness and reasonably
    // concludes one of them is broken.
    title: "Offers given, over the approaches the talent wrote back in",
  },
  { key: "chosen", label: "Chosen", numeric: true },
];

/** Dimension values as filter options. UNKNOWN keeps its own labelled entry: a
 *  coverage gap an operator can select is a gap someone investigates. */
function plainOptions(values: readonly string[]): MultiSelectOption[] {
  return values.map((v) => ({ value: v, label: v === UNKNOWN ? "Unknown" : v }));
}

/** Is-managed is a tri-state, not a boolean: null means we never observed the
 *  seller's subscription, which is different from observing that they have none. */
const MANAGED_OPTIONS: readonly MultiSelectOption[] = [
  { value: "yes", label: "Managed" },
  { value: "no", label: "Not managed" },
  { value: UNKNOWN, label: "Unknown" },
];

function SellerTable({
  rows,
  sort,
  onSort,
  remaining,
  onShowMore,
}: {
  rows: TalentSellerAggregate[];
  sort: { key: SortKey; dir: "asc" | "desc" };
  onSort: (key: SortKey) => void;
  remaining: number;
  onShowMore: () => void;
}) {
  // Sorting and paging are the PAGE's state now, not this component's: they are
  // part of the selection the server answers, so they have to live where the
  // selection is built. A local sort here would reorder 200 rows and quietly claim
  // to be a global order — which is exactly the "partial order reading as a global
  // one" the console's own rules warn about.
  if (rows.length === 0) {
    return (
      <EmptyState title="No sellers match" message="Try a wider date range or clearing a filter." />
    );
  }

  return (
    <div className="rc-table-wrap">
      <table className="rc-table tm-table">
        <TableExport name="talent-management" label="the talent dashboard" />
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.key} className={c.numeric ? "rc-num" : undefined} title={c.title}>
                <button
                  type="button"
                  className="tm-sort"
                  aria-label={`Sort by ${c.label}`}
                  onClick={() => onSort(c.key)}
                >
                  {c.label}
                  {sort.key === c.key ? (
                    <span aria-hidden="true">{sort.dir === "asc" ? " \u2191" : " \u2193"}</span>
                  ) : null}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Divided ONCE, here, from the counts the server sent. The wire carries
            // numerators, denominators and (sum, count) pairs exactly as it always
            // did; only the aggregation moved.
            const rates = sellerRates(r);
            return (
            <tr key={r.sellerId}>
              <td>
                {/* Unscoped on purpose: from the dashboard the question is "who is
                    this talent overall", so the page opens on their whole history.
                    The Searches tab links the SAME page scoped to that search's
                    project, because there the question is about one engagement. */}
                <span className="rc-cell-strong">
                  <ExplorerTalentLink sellerId={r.sellerId} className="cs-primary-link">
                    {r.sellerId}
                  </ExplorerTalentLink>
                </span>
                {r.isManaged ? (
                  <Badge tone="neutral" size="sm" className="rc-count-badge">
                    Managed
                  </Badge>
                ) : null}
              </td>
              <td>
                {/* SEARCH TIER: the band over the seller-quality score SCOUT ranks by
                    WITHIN a fit group. It used to read the gateway's A-E gig-quality
                    grade, which is only one input to that score and which the gateway
                    almost never supplies, so every row said "Unknown". */}
                <Dim value={r.searchTier} />
              </td>
              <td className="rc-num">
                {/* The raw score behind the band, so two sellers in one band can still
                    be ordered. Null is UNKNOWN (never scored), not zero. */}
                {r.searchTierPoints == null ? (
                  <Dim value={null} />
                ) : (
                  <span title="Seller-quality points; orders talents within a fit group">
                    {r.searchTierPoints.toFixed(1)}
                  </span>
                )}
              </td>
              <td>
                <Dim value={r.country} />
              </td>
              <td>
                <Dim value={r.sellerLevel} />
              </td>
              <td>
                {/* The Fiverr taxonomy SERVICE name, resolved through the committed
                    id -> name seed. An id we hold no name for still renders as the raw
                    id rather than "Unknown": we know exactly which service it is, we
                    just cannot spell it, and hiding it would lose a real value. */}
                <Dim value={subCategoryLabel(r.subCategoryId)} />
              </td>
              <td className="rc-num">{r.approached.toLocaleString()}</td>
              <td className="rc-num">
                <Pct ratio={rates.responseRate} />
              </td>
              <td className="rc-num">
                <Minutes value={rates.avgResponseMinutes} />
              </td>
              <td className="rc-num">
                <Minutes value={rates.avgTimeToOfferMinutes} />
              </td>
              <td className="rc-num">{r.questions.toLocaleString()}</td>
              <td className="rc-num">
                {/* Offers over the approaches this talent WROTE BACK in. A talent who
                    never wrote back has no willingness to report, so the cell reads
                    "-" rather than 0%: silence is a reachability signal, and the
                    Response rate column beside it is where that already shows. */}
                <Pct
                  ratio={rates.willingness}
                  unknownTitle="This talent never wrote back in this window"
                />
              </td>
              <td className="rc-num">
                {r.chosen.toLocaleString()} <Pct ratio={rates.chosenRate} />
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      <ShowMoreRow remaining={remaining} onClick={onShowMore} />
    </div>
  );
}

export function TalentManagementPage() {
  // Restored across a history BACK hop, and it has to be ALL of these together, not
  // some of them: this state IS the selection the server answers, so restoring the
  // sort and the window without the filters and the range would put a reader 300
  // rows down a list they never built. The pieces move together or none of them
  // should.
  const [filters, setFiltersRaw] = useRestoredState<Filters>(
    "talent-management/filters",
    EMPTY_FILTERS,
  );
  // Opens on the last 30 days, as it always has. Computed ONCE on mount rather
  // than per render: the range is editable, so recomputing it would quietly
  // overwrite whatever the reader picked on the next render. A restored range
  // skips the initializer for the same reason.
  const [range, setRangeRaw] = useRestoredState<DateRange>("talent-management/range", () =>
    rangeForDays(30),
  );
  const [sort, setSortRaw] = useRestoredState<{ key: SortKey; dir: "asc" | "desc" }>(
    "talent-management/sellers/sort",
    { key: "approached", dir: "desc" },
  );

  // Real offset paging, accumulated. Each "Show more" asks the server for the NEXT
  // page and appends it — it does not widen one request. That matters: a widening
  // request would stop at the server's page ceiling and leave the reader told there
  // are more rows with no way to reach them, which is a capped read wearing a paged
  // read's clothes.
  //
  // Not restored across a back hop, deliberately: how far you scrolled is a
  // statement ABOUT a row set, and it is meaningless against a list rebuilt by a
  // fresh query.
  const [offset, setOffset] = useState(0);
  // Pages held BY OFFSET rather than concatenated, because the same offset can be
  // answered more than once: the POST returns a cached answer, the server enqueues a
  // rebuild behind it, and the fresher answer arrives on Pusher as a NEW object for
  // the SAME page. Appending on result identity showed those 200 rows twice and made
  // "Showing 400 of N" a lie. Keyed by offset the re-answer simply REPLACES its own
  // page, which is also what makes a refreshed page update in place.
  const [pages, setPages] = useState<Map<number, TalentSellerAggregate[]>>(new Map());

  // Every control that changes WHICH rows come back resets the window with it. A
  // reader who narrows to Portugal and is still "showing 600" is looking at a
  // number about the list they just left.
  // The last answer we actually rendered, held so a FURTHER page does not blank the
  // screen. `offset` is part of the selection, so "Show more" is a new query key with
  // no cached data — without this the strip and the whole table drop to skeletons for
  // the 1-3s the warehouse takes, and the accumulated pages are on screen for none of
  // it, which is the entire thing the accumulation buys.
  //
  // Cleared by `restart`, deliberately: a filter, range or sort change makes these
  // numbers describe a population the reader is no longer looking at, and rendering
  // old totals under new filters is how someone screenshots the wrong thing. Held
  // ACROSS a page load only, where the selection has not changed.
  const [held, setHeld] = useState<TalentQueryResult | null>(null);

  const setFilters = setFiltersRaw;
  const setRange = setRangeRaw;
  const onSort = useCallback(
    (key: SortKey) =>
      setSortRaw((cur) =>
        cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" },
      ),
    [setSortRaw],
  );

  // The search box is the ONE control here that changes per KEYSTROKE, and every
  // change to the selection is a warehouse round trip now. Undebounced, typing
  // "alexandra" was nine POSTs, nine Cloud Tasks and eighteen BigQuery statements --
  // and, because each one resets the accumulation, nine collapses of the whole page
  // to a skeleton and back. The browser fold made this free; it is not free any more.
  // 300ms is the delay every other server-searched list in this console uses
  // (`lib/use-debounced.ts` says so in as many words).
  //
  // The INPUT still binds to `filters.q`, so typing stays instant; only the SELECTION
  // waits.
  const debouncedQ = useDebounced(filters.q.trim(), 300);

  const selection = useMemo(
    () => selectionFor(range, { ...filters, q: debouncedQ }, sort.key, sort.dir, PAGE_SIZE, offset),
    [range, filters, debouncedQ, sort, offset],
  );

  // Reset the accumulation whenever the QUESTION changes -- derived from the
  // selection itself rather than hung off each setter, so a control added later
  // cannot forget to do it, and so the debounced search resets once per pause
  // instead of once per character. A reader who narrows to Portugal and is still
  // "showing 600" is looking at a number about the list they just left.
  const baseKey = JSON.stringify({ ...selection, offset: 0 });
  useEffect(() => {
    setOffset(0);
    setPages(new Map());
    setHeld(null);
  }, [baseKey]);
  const metrics = useTalentResult(selection);
  const answer = metrics.result;
  // What the page renders from: this answer, or the last one for the same selection
  // while a further page is in flight.
  const result = answer ?? held;

  useEffect(() => {
    if (answer) setHeld(answer);
  }, [answer]);

  // Record each answered page under its own offset. Idempotent by construction: a
  // page answered twice overwrites itself rather than accumulating, so neither a
  // re-render nor a background refresh can duplicate rows. Safe against a loop for
  // the same reason it is idempotent -- writing an equal page is skipped, so the
  // state does not change and the effect does not re-fire.
  const answered = answer;
  useEffect(() => {
    if (!answered) return;
    setPages((prev) => {
      if (prev.get(offset) === answered.rows) return prev;
      const next = new Map(prev);
      next.set(offset, answered.rows);
      return next;
    });
  }, [answered, offset]);

  // Flattened in offset order, so the rows read in the order the server sorted them
  // however out of order the pages happened to land.
  const rows = useMemo(
    () =>
      pages.size > 0
        ? [...pages.keys()].sort((a, b) => a - b).flatMap((k) => pages.get(k) ?? [])
        : (result?.rows ?? []),
    [pages, result],
  );
  // The strip's four figures, over the WHOLE selection rather than the page. That
  // distinction is the point of moving the fold server-side: narrowing to Portugal
  // still narrows the headline numbers even though the table holds 200 rows.
  const totals = result ? stripFigures(result.totals) : null;
  const filtered = hasActiveFilters(filters);

  // Sub-category filters by id and reads as a name, so the list is ordered by the
  // NAME an operator is scanning. Ids we hold no name for keep their number and
  // sort with it, which is the only stable thing left to sort them by.
  const subCategoryOptions = useMemo(
    () =>
      (result?.facets.subCategory ?? [])
        .map((v) => ({
          value: v,
          label: v === UNKNOWN ? "Unknown" : (subCategoryLabel(Number(v)) ?? v),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [result],
  );

  if (metrics.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary area="talent_management" error={metrics.errorObject} />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Talent Management</h1>
        <p className="ad-page__sub">
          How talent responds to outreach. Every number comes from the analytics warehouse.
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      {/* The same control every other admin tab filters by, so "last 30 days"
          means the same thing here as on Searches or Alerts. It is part of the
          SELECTION now, so changing it asks the warehouse a new question rather
          than re-folding rows the page already had. */}
      <div className="tm-range" role="group" aria-label="Date range">
        <DateRangeFilter label="Approached" value={range} onChange={setRange} />
      </div>

      {/* Skeletons only when there is nothing to show. A further page keeps the
          table and the strip on screen; blanking them would discard the accumulation
          for the whole 1-3s the warehouse takes. */}
      {rows.length === 0 && (metrics.isLoading || metrics.isPending) ? (
        <>
          <div className="tm-stats" role="status" aria-busy="true">
            {[0, 1, 2, 3].map((k) => (
              <Card key={k} padding="md" className="tm-stat">
                <Skeleton variant="text" width="58%" />
                <Skeleton variant="text" width="36%" height={28} />
              </Card>
            ))}
          </div>
          <TableSkeleton columns={12} />
        </>
      ) : result === null || totals === null ? (
        <EmptyState
          title="No numbers for this view"
          message={
            metrics.error ??
            "The analytics warehouse could not answer this selection. Try a wider date range, or fewer filters."
          }
        />
      ) : (
        <>

          <section className="tm-stats" aria-label="Talent statistics">
            <StatCard
              label="Response rate"
              value={
                totals.responseRate.value === null
                  ? "-"
                  : `${Math.round(totals.responseRate.value * 100)}%`
              }
              hint={
                result.replyIncludesAutoRepliesCaveat
                  ? "Includes automatic replies and PA messages"
                  : `${totals.responseRate.numerator} of ${totals.responseRate.denominator} approached`
              }
            />
            {/* The median is the MIDDLE SELLER's own average, not a per-thread
                middle: the wire ships additive sums and counts, never raw
                per-thread minutes, so a true per-thread median is not computable
                here at all. The hint says "across sellers" for exactly that
                reason, and the two must keep saying the same thing. */}
            <StatCard
              label="Avg response time"
              value={minutesTextOrDash(totals.avgResponseMinutes)}
              median={minutesTextOrDash(totals.medianResponseMinutes)}
              hint="From Mira's first message, median across sellers"
            />
            <StatCard
              label="Avg time to offer"
              value={minutesTextOrDash(totals.avgTimeToOfferMinutes)}
              median={minutesTextOrDash(totals.medianTimeToOfferMinutes)}
              hint="First message to offer created, median across sellers"
            />
            <StatCard
              label="Questions per project"
              // TWO decimals, alone among the four cards, because an escalated question
              // is RARE: 12 over 590 approaches is 0.02, and at one decimal this card
              // printed a permanent "0.0" that read as a broken metric rather than as a
              // real and very small number. Verified against mira_kpi_dev end to end
              // (2026-07-30) -- the pipeline is healthy, the value is just tiny.
              value={
                totals.questionsPerProject === null ? "-" : totals.questionsPerProject.toFixed(2)
              }
              // The raw count behind the rate, so the reader can see what 0.02 is made
              // of without doing the arithmetic back.
              hint={`${formatStatValue(totals.questions)} escalated to the client, over ${formatStatValue(totals.projectsApproached)} approaches`}
            />
          </section>

          {filtered ? (
            <p className="tm-stats__note" role="status">
              These figures cover the {result.totalSellers.toLocaleString()} sellers that match
              your filters, not the whole population.
            </p>
          ) : null}

          <Provenance result={result} range={range} refreshing={metrics.refreshing} />

          <section className="ad-section">
            <div className="tm-section-head">
              <h2 className="ad-section__title">Sellers approached</h2>
              {/* Rows ON SCREEN over rows the selection MATCHES. The second number
                  comes from the server's own count over the filtered set, so it is
                  the real total and not "how many we happened to send". */}
              <span className="tm-section-head__note">
                Showing {rows.length.toLocaleString()} of {result.totalSellers.toLocaleString()}
              </span>
            </div>

            <div className="tm-filters">
              <Input
                type="search"
                inputSize="sm"
                placeholder="Filter by seller…"
                aria-label="Filter by seller"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
              <MultiSelect
                label="Filter by tier"
                allLabel="All tiers"
                options={plainOptions(result.facets.searchTier)}
                selected={filters.searchTier}
                onChange={(searchTier) => setFilters((f) => ({ ...f, searchTier }))}
              />
              <MultiSelect
                label="Filter by country"
                allLabel="All countries"
                options={plainOptions(result.facets.country)}
                selected={filters.country}
                onChange={(country) => setFilters((f) => ({ ...f, country }))}
              />
              <MultiSelect
                label="Filter by level"
                allLabel="All levels"
                options={plainOptions(result.facets.sellerLevel)}
                selected={filters.sellerLevel}
                onChange={(sellerLevel) => setFilters((f) => ({ ...f, sellerLevel }))}
              />
              <MultiSelect
                label="Filter by sub-category"
                allLabel="All sub-categories"
                options={subCategoryOptions}
                selected={filters.subCategory}
                onChange={(subCategory) => setFilters((f) => ({ ...f, subCategory }))}
              />
              <MultiSelect
                label="Filter by managed"
                allLabel="Managed: any"
                options={MANAGED_OPTIONS}
                selected={filters.managed}
                onChange={(managed) => setFilters((f) => ({ ...f, managed }))}
              />
              {filtered ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>

            <SellerTable
              rows={rows}
              sort={sort}
              onSort={onSort}
              remaining={Math.max(0, result.totalSellers - rows.length)}
              onShowMore={() => setOffset(rows.length)}
            />
          </section>
        </>
      )}
    </div>
  );
}
